import { ImagePlus, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { productService } from '../services/productService';
import { fieldClass, labelClass } from '../utils/form';

type Props = {
  label: string;
  value: string;
  onChange: (url: string) => void;
  hint?: string;
};

/** Upload image via /admin/uploads or paste a URL. */
export function ImageUploadField({ label, value, onChange, hint }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('فقط فایل تصویر مجاز است');
      return;
    }
    setUploading(true);
    try {
      const res = await productService.uploadFile(file);
      onChange(res.url);
      toast.success('تصویر آپلود شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'آپلود ناموفق بود');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className={labelClass}>{label}</label>
      {value ? (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <img src={value} alt="" className="h-40 w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute left-2 top-2 rounded-lg bg-white/90 px-2 py-1 text-xs font-medium text-slate-700 shadow"
          >
            حذف
          </button>
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400">
          هنوز تصویری انتخاب نشده
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          آپلود تصویر
        </button>
      </div>
      <input
        className={fieldClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="یا آدرس تصویر را وارد کنید…"
        dir="ltr"
      />
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}
